import { createFileRoute, useParams, useSearch } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { Loader2, Sparkles, Save, Plus, RefreshCw } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  listWorkspaceProducts,
  aiSuggestProductClassifications,
  saveProductClassifications,
  addManualProduct,
  REVENUE_TYPES,
  TIER_CATEGORIES,
  PAYMENT_STRUCTURES,
  type ProductClassification,
} from "@/lib/products.functions";
import { runReconciliation } from "@/lib/imports.functions";
import {
  regenerateSchedulesForWorkspace,
  reconcileSchedulesForWorkspace,
  detectTierDowngrades,
} from "@/lib/payment-schedules.functions";
import { formatCurrency } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/workspaces/$workspaceId/products")({
  component: ProductsPage,
  validateSearch: z.object({ focus: z.string().optional() }),
});

type Row = {
  key: string;
  product: string;
  amount: number;
  count: number;
  classification: ProductClassification | null;
  suggested_second_tier: boolean;
  manual?: boolean;
};

function ProductsPage() {
  const { workspaceId } = useParams({ from: "/_authenticated/workspaces/$workspaceId/products" });
  const fetchProducts = useServerFn(listWorkspaceProducts);
  const aiSuggest = useServerFn(aiSuggestProductClassifications);
  const save = useServerFn(saveProductClassifications);
  const addManual = useServerFn(addManualProduct);
  const qc = useQueryClient();

  const regenSchedules = useServerFn(regenerateSchedulesForWorkspace);
  const reconcile = useServerFn(reconcileSchedulesForWorkspace);
  const detectDg = useServerFn(detectTierDowngrades);
  const fullReconcile = useServerFn(runReconciliation);

  const { data, isLoading } = useQuery({
    queryKey: ["products", workspaceId],
    queryFn: () => fetchProducts({ data: { workspace_id: workspaceId } }),
  });

  const [edits, setEdits] = useState<Record<string, ProductClassification>>({});
  const [aiReasonings, setAiReasonings] = useState<Record<string, string>>({});
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualProduct, setManualProduct] = useState("");
  const [manualAmount, setManualAmount] = useState("");
  const [manualRevenue, setManualRevenue] = useState<typeof REVENUE_TYPES[number]>("recurring");
  const [manualTier, setManualTier] = useState<typeof TIER_CATEGORIES[number]>("high_ticket");
  const [showUnclassifiedOnly, setShowUnclassifiedOnly] = useState(false);
  const [savingManual, setSavingManual] = useState(false);

  const search = useSearch({ from: "/_authenticated/workspaces/$workspaceId/products" }) as { focus?: string };
  const focusKey = search?.focus;
  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
  const [highlightKey, setHighlightKey] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    const seed: Record<string, ProductClassification> = {};
    for (const r of data.items as Row[]) {
      if (r.classification) seed[r.key] = r.classification;
    }
    setEdits(seed);
  }, [data]);

  useEffect(() => {
    if (!focusKey || !data) return;
    const el = rowRefs.current[focusKey];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightKey(focusKey);
      const t = setTimeout(() => setHighlightKey(null), 2500);
      return () => clearTimeout(t);
    }
  }, [focusKey, data]);

  if (isLoading) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  const items = (data?.items ?? []) as Row[];

  const update = (key: string, patch: Partial<ProductClassification>) => {
    setEdits((prev) => {
      const cur = prev[key] ?? { revenue_type: "one_time", tier_category: "front_end", is_second_tier: false } as ProductClassification;
      return { ...prev, [key]: { ...cur, ...patch } };
    });
  };

  const runAi = async () => {
    setAiLoading(true);
    try {
      const unclassified = items.filter((r) => !edits[r.key]);
      if (unclassified.length === 0) {
        toast.info("Everything's classified already.");
        return;
      }
      const res = await aiSuggest({
        data: {
          workspace_id: workspaceId,
          items: unclassified.map((r) => ({ key: r.key, product: r.product, amount: r.amount, count: r.count })),
        },
      });
      if (res.error) throw new Error(res.error);
      const next = { ...edits };
      const reasonings: Record<string, string> = { ...aiReasonings };
      for (const s of res.suggestions as any[]) {
        const patch: Partial<ProductClassification> = {
          revenue_type: s.revenue_type,
          tier_category: s.tier_category,
          is_second_tier: s.is_second_tier,
        };
        if (s.payment_structure) patch.payment_structure = s.payment_structure;
        if (s.renewal_product_key != null) patch.renewal_product_key = s.renewal_product_key;
        if (s.num_payments != null) patch.num_payments = s.num_payments;
        if (s.payment_cadence_days != null) patch.payment_cadence_days = s.payment_cadence_days;
        if ((s as any).renewal_cadence_days != null) patch.renewal_cadence_days = (s as any).renewal_cadence_days;
        if ((s as any).renewal_delay_days != null) patch.renewal_delay_days = (s as any).renewal_delay_days;
        next[s.key] = { ...(next[s.key] ?? {} as ProductClassification), ...patch };
        if (s.reasoning) reasonings[s.key] = s.reasoning;
      }
      setEdits(next);
      setAiReasonings(reasonings);
      const src = (res as any).sources ?? { cache: 0, pattern: 0, ai: res.suggestions.length };
      const parts: string[] = [];
      if (src.ai > 0) parts.push(`${src.ai} from AI`);
      if (src.pattern > 0) parts.push(`${src.pattern} from learned patterns`);
      if (src.cache > 0) parts.push(`${src.cache} from cache`);
      toast.success(`AI filled ${res.suggestions.length} suggestions${parts.length ? ` (${parts.join(", ")})` : ""}, review reasoning column and save.`);
    } catch (e: any) {
      toast.error(e.message ?? "AI suggest failed");
    } finally {
      setAiLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await save({ data: { workspace_id: workspaceId, classifications: edits } });
      toast.success(`Saved ${res.saved} products. ${res.contacts_updated} contacts updated. MRR recalculated.`);
      qc.invalidateQueries({ queryKey: ["products", workspaceId] });
      qc.invalidateQueries({ queryKey: ["dashboard", workspaceId] });
    } catch (e: any) {
      toast.error(e.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleReconcile = async () => {
    setReconciling(true);
    try {
      const gen = await regenSchedules({ data: { workspace_id: workspaceId } });
      const rec = await reconcile({ data: { workspace_id: workspaceId, with_ai: true } });
      const dg = await detectDg({ data: { workspace_id: workspaceId, with_ai: true } });
      const seg = await fullReconcile({ data: { workspace_id: workspaceId } });
      toast.success(`${gen.message} Segments: ${seg.counts.active_paid} active · ${seg.counts.upgraded_paid} upgraded · ${seg.counts.cancelled_paid} cancelled. ${rec.flagged} schedule + ${dg.flagged} downgrade anomalies.`);
      qc.invalidateQueries({ queryKey: ["dashboard", workspaceId] });
      qc.invalidateQueries({ queryKey: ["contacts", workspaceId] });
    } catch (e: any) {
      toast.error(e.message ?? "Reconcile failed");
    } finally {
      setReconciling(false);
    }
  };

  const handleAddManual = async () => {
    const amt = parseFloat(manualAmount);
    if (!manualProduct.trim() || !isFinite(amt) || amt < 0) {
      toast.error("Enter a product name and valid amount.");
      return;
    }
    setSavingManual(true);
    try {
      await addManual({
        data: {
          workspace_id: workspaceId,
          product: manualProduct.trim(),
          amount: amt,
          classification: {
            revenue_type: manualRevenue,
            tier_category: manualTier,
            is_second_tier: false,
          },
        },
      });
      toast.success(`Added ${manualProduct} (${formatCurrency(amt)}). Configure payment structure below.`);
      setManualOpen(false);
      setManualProduct("");
      setManualAmount("");
      qc.invalidateQueries({ queryKey: ["products", workspaceId] });
    } catch (e: any) {
      toast.error(e.message ?? "Failed to add product");
    } finally {
      setSavingManual(false);
    }
  };

  const unclassifiedCount = items.filter((r) => !edits[r.key]).length;
  const visibleItems = (showUnclassifiedOnly ? items.filter((r) => !edits[r.key]) : items)
    .slice()
    .sort((a, b) => {
      const aU = !edits[a.key] ? 0 : 1;
      const bU = !edits[b.key] ? 0 : 1;
      if (aU !== bU) return aU - bU;
      return b.count - a.count;
    });
  const productOptions = items.map((r) => ({ key: r.key, label: `${r.product}, ${formatCurrency(r.amount)}` }));

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Products & Tiers</h2>
          <p className="text-sm text-muted-foreground">
            Classify each product so MRR only sums recurring revenue, and configure installment plans so renewals can be tracked. {unclassifiedCount > 0 && (
              <span className="text-foreground font-medium">{unclassifiedCount} unclassified.</span>
            )}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {unclassifiedCount > 0 && (
            <label className="flex items-center gap-2 text-xs text-muted-foreground mr-2 cursor-pointer">
              <Switch checked={showUnclassifiedOnly} onCheckedChange={setShowUnclassifiedOnly} />
              Show unclassified only
            </label>
          )}
          <Button variant="outline" onClick={() => setManualOpen(true)}>
            <Plus className="h-4 w-4 mr-2" /> Add manual product
          </Button>
          <Button variant="outline" onClick={runAi} disabled={aiLoading}>
            {aiLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            Suggest with AI
          </Button>
          <Button variant="outline" onClick={handleReconcile} disabled={reconciling}>
            {reconciling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Regenerate + reconcile
          </Button>
          <Button onClick={handleSave} disabled={saving || Object.keys(edits).length === 0}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save & recalculate
          </Button>
        </div>
      </div>

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add manual product</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Product name</Label>
              <Input value={manualProduct} onChange={(e) => setManualProduct(e.target.value)} placeholder="AI Secrets Challenge Platinum Monthly" />
            </div>
            <div>
              <Label>Commission amount per payment</Label>
              <Input type="number" step="0.01" value={manualAmount} onChange={(e) => setManualAmount(e.target.value)} placeholder="118.80" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Revenue type</Label>
                <Select value={manualRevenue} onValueChange={(v: any) => setManualRevenue(v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REVENUE_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tier category</Label>
                <Select value={manualTier} onValueChange={(v: any) => setManualTier(v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIER_CATEGORIES.map((t) => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">After adding, configure payment structure (recurring/installment) in the table below, then Save & recalculate.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setManualOpen(false)}>Cancel</Button>
            <Button onClick={handleAddManual} disabled={savingManual}>
              {savingManual ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Add product
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead className="w-28">Amount</TableHead>
                <TableHead className="w-20">Records</TableHead>
                <TableHead className="w-40">Revenue type</TableHead>
                <TableHead className="w-40">Tier category</TableHead>
                <TableHead className="w-28">2nd tier</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleItems.map((r) => {
                const c = edits[r.key];
                const isClassified = !!c;
                return (
                  <TableRow
                    key={r.key}
                    ref={(el) => { rowRefs.current[r.key] = el; }}
                    className={`${!isClassified ? "bg-muted/30" : ""} ${highlightKey === r.key ? "ring-2 ring-amber-500 ring-offset-2 ring-offset-background transition-shadow" : ""}`}
                  >
                    <TableCell className="font-medium">
                      {r.product}
                      {!isClassified && <Badge variant="secondary" className="ml-2 text-xs">unclassified</Badge>}
                      {r.suggested_second_tier && !isClassified && (
                        <Badge variant="outline" className="ml-2 text-xs">likely 2nd tier</Badge>
                      )}
                      {aiReasonings[r.key] && (
                        <div className="mt-1 text-xs italic text-muted-foreground max-w-xl">
                          <span className="font-semibold not-italic">AI:</span> {aiReasonings[r.key]}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>{formatCurrency(r.amount)}</TableCell>
                    <TableCell>{r.count}</TableCell>
                    <TableCell>
                      <Select
                        value={c?.revenue_type ?? ""}
                        onValueChange={(v: any) => update(r.key, { revenue_type: v })}
                      >
                        <SelectTrigger className="h-8"><SelectValue placeholder="Pick…" /></SelectTrigger>
                        <SelectContent>
                          {REVENUE_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={c?.tier_category ?? ""}
                        onValueChange={(v: any) => update(r.key, { tier_category: v })}
                      >
                        <SelectTrigger className="h-8"><SelectValue placeholder="Pick…" /></SelectTrigger>
                        <SelectContent>
                          {TIER_CATEGORIES.map((t) => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={c?.is_second_tier ?? r.suggested_second_tier}
                        onCheckedChange={(v) => update(r.key, { is_second_tier: v })}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={c?.notes ?? ""}
                        onChange={(e) => update(r.key, { notes: e.target.value })}
                        placeholder="Optional"
                        className="h-8"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    No products yet. Import a CSV first.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Installment / renewal config */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div>
            <h3 className="text-sm font-semibold">Payment structure & renewals</h3>
            <p className="text-xs text-muted-foreground">
              Configure installment plans (e.g. 3-pay) and what they renew into. Drives payment schedules, MRR projection, and failed-renewal anomalies.
            </p>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead className="w-44">Structure</TableHead>
                <TableHead className="w-24"># payments</TableHead>
                <TableHead className="w-32">Cadence (days)</TableHead>
                <TableHead className="w-56">Renews into</TableHead>
                <TableHead className="w-32">Renewal delay (days)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.filter((r) => edits[r.key]?.revenue_type !== "one_time").map((r) => {
                const c = edits[r.key];
                if (!c) return null;
                const struct = c.payment_structure ?? "single";
                const isInstallment = struct === "installment_ends" || struct === "installment_renews";
                // Derive display value from structure + cadence. New picker presents Monthly/Quarterly/
                // Annual/Custom shortcuts that all map to payment_structure="recurring" with a preset cadence.
                let displayValue: string = struct;
                if (struct === "recurring") {
                  const cad = c.payment_cadence_days;
                  if (cad === 30) displayValue = "monthly";
                  else if (cad === 90) displayValue = "quarterly";
                  else if (cad === 365) displayValue = "annual";
                  else displayValue = "custom_recurring";
                }
                const isCustomRecurring = displayValue === "custom_recurring";
                const cadenceEditable = isInstallment || isCustomRecurring;
                const onStructureChange = (v: string) => {
                  switch (v) {
                    case "monthly":
                      update(r.key, { payment_structure: "recurring", payment_cadence_days: 30 });
                      break;
                    case "quarterly":
                      update(r.key, { payment_structure: "recurring", payment_cadence_days: 90 });
                      break;
                    case "annual":
                      update(r.key, { payment_structure: "recurring", payment_cadence_days: 365 });
                      break;
                    case "custom_recurring":
                      update(r.key, { payment_structure: "recurring", payment_cadence_days: c.payment_cadence_days ?? null });
                      break;
                    case "single":
                      update(r.key, { payment_structure: "single", payment_cadence_days: null });
                      break;
                    case "installment_ends":
                    case "installment_renews":
                      update(r.key, { payment_structure: v as any });
                      break;
                  }
                };
                return (
                  <TableRow key={r.key}>
                    <TableCell className="font-medium">{r.product} <span className="text-muted-foreground">, {formatCurrency(r.amount)}</span></TableCell>
                    <TableCell>
                      <Select value={displayValue} onValueChange={onStructureChange}>
                        <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="single">Single payment</SelectItem>
                          <SelectItem value="monthly">Monthly subscription</SelectItem>
                          <SelectItem value="quarterly">Quarterly subscription</SelectItem>
                          <SelectItem value="annual">Annual subscription</SelectItem>
                          <SelectItem value="custom_recurring">Custom recurring</SelectItem>
                          <SelectItem value="installment_ends">Installment (then ends)</SelectItem>
                          <SelectItem value="installment_renews">Installment (then renews)</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number" min={1} max={60}
                        disabled={!isInstallment}
                        value={c.num_payments ?? ""}
                        onChange={(e) => update(r.key, { num_payments: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
                        className="h-8"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number" min={1} max={3650}
                        disabled={!cadenceEditable}
                        value={c.payment_cadence_days ?? ""}
                        onChange={(e) => update(r.key, { payment_cadence_days: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
                        placeholder="30"
                        className="h-8"
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        value={c.renewal_product_key ?? "__none__"}
                        onValueChange={(v) => update(r.key, { renewal_product_key: v === "__none__" ? null : v })}
                        disabled={struct !== "installment_renews"}
                      >
                        <SelectTrigger className="h-8"><SelectValue placeholder="Pick renewal product…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">None</SelectItem>
                          {productOptions.filter((p) => p.key !== r.key).map((p) => (
                            <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number" min={0} max={3650}
                        disabled={struct !== "installment_renews"}
                        value={c.renewal_delay_days ?? ""}
                        onChange={(e) => update(r.key, { renewal_delay_days: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
                        placeholder="30"
                        className="h-8"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <p className="text-xs text-muted-foreground">
            Tip: For Prime Mover Platinum 3-pay → 1-month cadence, 3 payments, renews into the $118.80/mo Monthly product with 30-day delay.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
